import LoadingBar from "@components/ui/LoadingBar";
import Navigation from "@navigation/index";
import WhatsAppStatusBanner from "@components/whatsapp/WhatsAppStatusBanner";
import firebase from "@services/firebase";
import { useAuthStore } from "@store/auth";
import { useDataStore } from "@store/user_org";
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import Gleap from "gleap";

const isProd = process.env.REACT_APP_ENV === "prod";

/** True on a phone, in either orientation.
 *
 *  A width test alone missed a phone held sideways (≈844 px wide on an iPhone), which
 *  is exactly where the update banner hurts most: it covers half the screen. So the
 *  SHORT side of the viewport decides, and a coarse pointer keeps a narrow desktop
 *  window — which has room for the banner — out of it. */
function isPhoneViewport(): boolean {
  const { innerWidth: w, innerHeight: h } = window;
  if (w < 768) return true;
  const coarse = window.matchMedia?.("(pointer: coarse)").matches ?? false;
  return coarse && Math.min(w, h) < 768;
}

type MaintenanceData = {
  active: boolean;
  message: string;
  estimated_end: string;
};

export default function App() {
  const user = useAuthStore((state) => state.user);
  const initAuthListener = useAuthStore((state) => state.initAuthListener);
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  const returnToAdmin = useAuthStore((state) => state.returnToAdmin);
  const fetchUserData = useDataStore((state) => state.fetchUserData);
  const clearAllData = useDataStore((state) => state.clearAllData);
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);

  const [localVersion, setLocalVersion] = useState<number | null>(null);
  const [showRefreshBanner, setShowRefreshBanner] = useState(false);
  // The update banner sits above the content and can hide what the user is doing.
  // Minimising shrinks it to a pill above the Gleap button — without updating.
  const [refreshMinimized, setRefreshMinimized] = useState(false);
  // Les écrans mobiles n'affichent ni le bouton Gleap ni la bannière de mise à jour.
  const [isMobile, setIsMobile] = useState(isPhoneViewport);
  const [maintenanceData, setMaintenanceData] =
    useState<MaintenanceData | null>(null);
  const unsubscribeVersion = useRef<(() => void) | null>(null);

  const isDevOrg = orgData?.id === "OJjnaOZpttbpnWS2Vb6u";
  const isMaintenanceProof = userData?.config?.maintenance_proof === true;

  useEffect(() => {
    const handleResize = () => setIsMobile(isPhoneViewport());
    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  // Initialize Gleap — hide on mobile
  useEffect(() => {
    Gleap.initialize("jw2U742uBr9vmc9VFZuQxtl3YGnFtSuo");
  }, []);

  useEffect(() => {
    Gleap.showFeedbackButton(!isMobile);
  }, [isMobile]);

  // Identify user in Gleap
  useEffect(() => {
    if (userData) {
      Gleap.identify(userData.id, {
        name: userData.infos.full_name || userData.infos.display_name,
        email: userData.infos.email,
        phone: userData.infos.phone_number,
        companyId: userData.org_id,
        companyName: orgData?.infos?.name,
      });
    } else {
      Gleap.clearIdentity();
    }
  }, [userData, orgData]);

  // Initialize auth listener on mount
  useEffect(() => {
    const unsubscribe = initAuthListener();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Fetch user data when user logs in
  useEffect(() => {
    if (user?.uid) {
      fetchUserData(user.uid);
    } else if (user === null) {
      clearAllData();
    }
  }, [user?.uid]);

  // Local version baked into the JS bundle at CI build time (REACT_APP_VERSION).
  // We do NOT fetch /version.txt: GCS serves it cached and ignores query strings,
  // so a stale cached copy would pin localVersion below react_version and keep the
  // update banner stuck. The bundle is content-hashed and index.html is no-cache,
  // so a reload always loads the latest bundle carrying its own version number.
  useEffect(() => {
    const v = parseInt(process.env.REACT_APP_VERSION || "0", 10);
    if (v) setLocalVersion(v);
  }, []);

  // Listen to Firestore version — show banner when a new deployment is available
  useEffect(() => {
    if (localVersion === null) return;

    if (unsubscribeVersion.current) unsubscribeVersion.current();

    const versionRef = doc(firebase.db, "maintenance", "version");
    unsubscribeVersion.current = onSnapshot(versionRef, (snap) => {
      if (!snap.exists()) return;
      const remoteVersion: number = snap.data().react_version;
      if (isProd && remoteVersion > localVersion) {
        setShowRefreshBanner(true);
      }
    });

    return () => {
      if (unsubscribeVersion.current) unsubscribeVersion.current();
    };
  }, [localVersion]);

  // Listen to Firestore maintenance status in real-time
  useEffect(() => {
    const statusRef = doc(firebase.db, "maintenance", "status");
    const unsubscribe = onSnapshot(statusRef, (snap) => {
      if (!snap.exists()) {
        setMaintenanceData(null);
        return;
      }
      const data = snap.data() as MaintenanceData & { active: boolean };
      setMaintenanceData(data);
    });
    return () => unsubscribe();
  }, []);

  const showMaintenance =
    maintenanceData?.active === true && !isDevOrg && !isMaintenanceProof;

  const formatEstimatedEnd = (iso: string) => {
    try {
      const date = new Date(iso);
      return date.toLocaleString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (showMaintenance) {
    return (
      <div className="fixed inset-0 z-[9999] bg-neutral-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-8 max-w-md text-center px-6">
          <div className="flex flex-col items-center gap-3">
            <h1 className="text-xl font-semibold text-neutral-900">
              Maintenance en cours
            </h1>
            <p className="text-neutral-500 text-sm leading-relaxed">
              {maintenanceData.message}
            </p>
          </div>

          <div className="w-64">
            <LoadingBar />
          </div>

          {maintenanceData.estimated_end && (
            <p className="text-neutral-400 text-xs">
              Fin estimée : {formatEstimatedEnd(maintenanceData.estimated_end)}
            </p>
          )}

          <button
            onClick={() => useAuthStore.getState().logout()}
            className="mt-4 text-sm text-neutral-400 hover:text-neutral-600 underline transition-colors"
          >
            Se déconnecter
          </button>
        </div>

        <style>{`
          @keyframes maintenance-progress {
            0% { width: 0%; margin-left: 0; }
            50% { width: 60%; margin-left: 20%; }
            100% { width: 0%; margin-left: 100%; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      {isImpersonating && (
        <button
          onClick={() => { clearAllData(); returnToAdmin(); }}
          className="fixed top-0 left-0 right-0 z-[9999] w-full py-2 px-4 bg-amber-400 hover:bg-amber-500 transition-colors text-center text-h3 font-semibold text-amber-900"
        >
          Connecté en tant que {userData?.infos.full_name || userData?.infos.display_name || userData?.infos.email} — Cliquer pour revenir au compte admin
        </button>
      )}
      {/* WhatsApp outage banner — a dead session silently drops INCOMING client
          messages, so this must be visible on every page, not only the chat. */}
      {user && <WhatsAppStatusBanner />}
      <Navigation />
      {/* Pas sur la page de suivi : c'est le client qui la voit, pas l'équipe. */}
      {showRefreshBanner && isProd && !isMobile && !window.location.pathname.startsWith("/tracking") && (
        refreshMinimized ? (
          /* Minimised — bottom-[92px] keeps it clear of the Gleap button below. */
          <div
            onClick={() => window.location.reload()}
            title="Cliquez pour mettre à jour"
            className="fixed bottom-[92px] right-6 z-50 flex items-center gap-2 cursor-pointer rounded-full bg-neutral-900 py-2 pl-4 pr-2 shadow-lg text-white hover:-translate-y-0.5 transition-all duration-300"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            <span className="text-h3 font-medium">Mettre à jour</span>
            <button
              onClick={(e) => { e.stopPropagation(); setRefreshMinimized(false); }}
              aria-label="Agrandir"
              className="flex h-6 w-6 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-700 hover:text-white transition-colors"
            >
              <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6" /></svg>
            </button>
          </div>
        ) : (
          <div
            onClick={() => window.location.reload()}
            className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 cursor-pointer hover:-translate-y-1 hover:scale-105 transition-all duration-300"
          >
            <div className="px-6 py-4 bg-neutral-900 rounded-xl shadow-lg flex items-center gap-6 text-white">
              {/* Le logo de l'outil à gauche, comme sur les autres fronts. */}
              <img src="/logo_3.png" alt="" className="w-9 h-9 rounded-lg object-contain shrink-0 -mr-2" />
              <div className="flex flex-col gap-1">
                <div className="font-semibold text-h1">
                  Une nouvelle version est disponible.
                </div>
                <div className="text-h2 text-neutral-400">
                  Cliquez ici pour mettre à jour.
                </div>
              </div>
              {/* Bouton explicite : sans libellé, personne ne voyait qu'on
                  pouvait repousser la mise à jour.
                  stopPropagation : réduire ne doit surtout pas recharger la page */}
              <button
                onClick={(e) => { e.stopPropagation(); setRefreshMinimized(true); }}
                aria-label="Réduire — mettre à jour plus tard"
                title="Réduire — mettre à jour plus tard"
                className="flex shrink-0 items-center gap-1.5 rounded-lg border border-neutral-600 bg-neutral-800 px-3 py-2 text-h2 font-medium text-neutral-200 hover:bg-neutral-700 hover:text-white hover:border-neutral-500 transition-colors"
              >
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                Plus tard
              </button>
            </div>
          </div>
        )
      )}
    </>
  );
}
